/* @noflow FIXME */
import idx from 'idx';
import * as React from 'react';
import { connect } from 'react-redux';
import { mountModal } from 'lib/modal';
import { bindActionCreators } from 'redux';
import { showToast } from 'reducers/toasts';
import EventTracking from 'lib/EventTracking';
import { withRouter } from 'app/context/Router';
import SequentialProgressFooter from 'components/SequentialProgressFooter';
import AdminUpgradeTermsModal from 'admin/components/AdminUpgradeTermsModal';
import type { State as ReduxState, Fields } from 'admin/reducers/adminUpgradePage';
import { PAGE_INDEX_TO_TRACKING_PREFIX } from 'admin/components/AdminUpgradeForms';
import { largeOrgThreshold } from 'constants/SelfServeConstants';

import {
  PAGE_NUMBER,
  goNextPage,
  saveQuoteToBackend,
  goPreviousPage,
  nextButtonState,
  previousButtonState,
  reinitialize,
} from 'admin/reducers/adminUpgradePage';

import './styles/AdminUpgradeFooter.scss';

type Props = {
  router: *,
  referredFromSales: ?boolean,
  organizationUuid: ?string,
  reduxState: ReduxState,
  goNextPage: () => void,
  goPreviousPage: () => void,
  showToast: (text: string, kind: 'success' | 'error') => void,
  saveQuoteToBackend: (quoteUUID?: ?string, fields: Fields, pageIndex: number) => Promise<any>,
  reinitialize: () => void,
};

type State = {
  saving: boolean,
};

class AdminUpgradeFooter extends React.Component<Props, State> {
  state: State = {
    saving: false,
  };

  track = (key: string) => {
    const organization_uuid = this.props.organizationUuid || null;

    EventTracking.trackKey(
      `${PAGE_INDEX_TO_TRACKING_PREFIX[this.props.reduxState.pageIndex]}.${key}`,
      {
        organization_uuid,
      }
    );
  };

  handleFinished = (actionNeeded: boolean) => {
    this.props.router.push(`/admin_upgrade/done?action_needed=${actionNeeded}`);
    this.props.reinitialize();
  };

  handlePrevious = () => {
    this.track('back.click');
    this.props.goPreviousPage();
  };

  handleNext = () => {
    this.track('continue.click');

    const onSavedSuccess = () => {
      const isLastPage = this.props.reduxState.pageIndex === PAGE_NUMBER - 1;

      if (
        this.props.reduxState.fields.get('organizationAddress') &&
        this.props.reduxState.fields.get('organizationAddress').country === 'Canada'
      ) {
        this.props.router.push('/admin_upgrade/canadian_organization');
      } else if (
        this.props.reduxState.fields.get('studentsCount') > largeOrgThreshold &&
        !this.props.referredFromSales
      ) {
        this.props.router.push('/admin_upgrade/large_organization');
      } else if (isLastPage) {
        mountModal(<AdminUpgradeTermsModal onAgreed={this.handleFinished} />, this);
      } else {
        window.scrollTo(0, 0);
        this.props.goNextPage();
      }
    };

    this.setState({ saving: true });

    // the quote uuid, if null then save will create a new quote.
    const quoteUUID = idx(this.props.reduxState, _ => _.quote.uuid);

    this.props
      .saveQuoteToBackend(quoteUUID, this.props.reduxState.fields, this.props.reduxState.pageIndex)
      .then(result => {
        if (result.error) {
          this.setState({ saving: false });
          const { fieldErrors } = result.error;
          const errorMessage =
            fieldErrors && fieldErrors.length
              ? result.error.fieldErrors.map(f => f.message).join(', ')
              : result.error.message;
          this.props.showToast(errorMessage, 'error');
          const orgUuid = this.props.reduxState.fields.get('organizationUuid') || '';
          console.error(`AdminUpgradeFooter - ${errorMessage} - org uuid ${orgUuid}`);
        } else {
          this.setState({ saving: false });
          onSavedSuccess();
        }
      })
      .catch(e => {
        this.props.showToast('Oops, something went wrong saving the quote request.', 'error');
        console.debug(e, e && e.stack);
        console.error('AdminUpgradeFooter - hard fail - could not save form');
        this.setState({ saving: false });
      });
  };

  render() {
    const nextState = nextButtonState(this.props.reduxState);
    const previousState = previousButtonState(this.props.reduxState);

    return (
      <div className="AdminUpgradeFooter">
        <SequentialProgressFooter
          steps={PAGE_NUMBER}
          currentStep={this.props.reduxState.pageIndex + 1}
          onPrevious={this.handlePrevious}
          previous={!previousState.hidden}
          previousDisabled={previousState.disabled}
          previousButtonText={previousState.text}
          onNext={this.handleNext}
          next={!nextState.hidden}
          nextDisabled={nextState.disabled || this.state.saving}
          nextButtonText={nextState.text}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    reduxState: state.adminUpgradePage,
  };
}

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators(
    { showToast, saveQuoteToBackend, goPreviousPage, goNextPage, reinitialize },
    dispatch
  );
}

export { AdminUpgradeFooter };
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AdminUpgradeFooter));
